/**
 * Clone any modal that has the 'tv-modal' class and move them down to the bottom of the document.
 */
export class TVModal {
    modals: NodeListOf<HTMLElement>;
    cloned_modals: NodeListOf<HTMLElement>;

    constructor() {
        this.modals = document.querySelectorAll('.tv-modal');
        this.cloned_modals = document.querySelectorAll('.tv-cloned-modal');

        this.init();
    }

    /**
     * Run the functions to clone and move the modals.
     *
     * @returns {void}
     */
    init() : void {
        this.remove_previous_cloned();
        this.move();
    }

    /**
     * Bind the tabs in the modal.
     *
     * @param {HTMLElement} modal that has the tabs.
     * @returns {void}
     */
    async bind_tabs(modal : HTMLElement) : Promise<void> {
        const { ToggleContent } = await import('./toggle_content');
        const tabs = {
            links: modal.getElementsByClassName('js-modal-tablink'),
            mobile_links: modal.getElementsByClassName('tv-tablink__mobile'),
            content: modal.getElementsByClassName('js-modal-tab-content') as HTMLCollectionOf<HTMLElement>
        };

        (() => new ToggleContent(tabs.content, tabs, 0, false, null))();
    }

    /**
     * Clean up previous cloned modals.
     *
     * @returns {void}
     */
    remove_previous_cloned() : void {
        if (this.cloned_modals.length > 0) {
            for (const cloned_modal of this.cloned_modals) {
                cloned_modal.remove();
            }
        }
    }

    /**
     * Clone and move the modal to the bottom of the document.
     *
     * @returns {void}
     */
    move() : void {
        // Clone the original modal if there any.
        if (this.modals.length > 0) {
            for (const modal of this.modals) {
                // Modals marked js-tv-modal-move are moved as they are, because cloning a large modal is slow.
                // The rest are still cloned: code like AJAXToggle keeps a reference to the original element and
                // relies on it being detached by the clone.
                const move_modal = modal.classList.contains('js-tv-modal-move');
                const cloned_modal = move_modal ? modal : modal.cloneNode(true) as HTMLElement;
                cloned_modal.classList.remove('tv-modal');
                cloned_modal.classList.add('tv-cloned-modal');
                cloned_modal.classList.add('hidden');
                document.body.appendChild(cloned_modal);

                // Bind the tabs if the modal has a class indicating it does.
                if (cloned_modal.classList.contains('js-has-tabs')) {
                    this.bind_tabs(cloned_modal);
                }
                if (!move_modal) {
                    modal.remove();
                }
            }

            // If there's a .tv-modal inside another .tv-modal, it will get cloned twice (once with the parent,
            // once by itself). We need to check for any duplicate nested modals and delete them.
            const nested_modals = document.querySelectorAll('.tv-modal');
            if (nested_modals.length) {
                for (const modal of nested_modals) {
                    modal.remove();
                }
            }
        }
    }
}

export function init_tv_modal() : void {
    if (document.getElementsByClassName('tv-modal')) {
        (() => new TVModal())();
    }
}
